// While the share guide is open, check again by itself, so there is no button to press afterwards.
// Shared by Figmafolio's and Aero's guide.
//
//   const { done } = useSharePolling({ show, recheck, onShared, onClose })
//
// Re-checks every 3 seconds, immediately when this tab regains focus, and stops after 2 minutes.
// When the answer is 'viewable' it sets `done`, calls onShared once, and closes after a moment.
import { useEffect, useRef, useState } from 'react';

export const POLL_EVERY_MS = 3000;
export const POLL_STOP_AFTER_MS = 2 * 60 * 1000;
export const POLL_CLOSE_DELAY_MS = 1600;

export default function useSharePolling({ show, recheck, onShared, onClose }) {
  const [done, setDone] = useState(false);
  const recheckRef = useRef(recheck);
  recheckRef.current = recheck;
  const sharedRef = useRef(onShared);
  sharedRef.current = onShared;
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!show) {
      setDone(false);
      return undefined;
    }
    let cancelled = false;
    let busy = false;
    let closeTimer = null;
    const startedAt = Date.now();

    const tick = async () => {
      if (cancelled || busy) return;
      busy = true;
      let status = 'unknown';
      try {
        status = await recheckRef.current();
      } catch (e) {
        status = 'unknown';
      }
      busy = false;
      if (cancelled || status !== 'viewable') return;
      cancelled = true;
      setDone(true);
      if (sharedRef.current) sharedRef.current();
      closeTimer = setTimeout(() => closeRef.current && closeRef.current(), POLL_CLOSE_DELAY_MS);
    };

    const interval = setInterval(() => {
      if (Date.now() - startedAt > POLL_STOP_AFTER_MS) {
        clearInterval(interval);
        return;
      }
      tick();
    }, POLL_EVERY_MS);
    const onFocus = () => {
      if (Date.now() - startedAt <= POLL_STOP_AFTER_MS) tick();
    };
    window.addEventListener('focus', onFocus);

    return () => {
      cancelled = true;
      clearInterval(interval);
      clearTimeout(closeTimer);
      window.removeEventListener('focus', onFocus);
    };
  }, [show]);

  return { done };
}
